import {HttpParams} from "@angular/common/http";
import {inject, LOCALE_ID} from "@angular/core";
import {SERVER_APP_CONFIG, WEB_APP_CONFIG} from "../../../common/app-config";
import type {IPaginationQuery} from "../../models/IPaginated";

export type QueryValue = string | number | boolean;
export type QueryParameters = Record<string, QueryValue | readonly QueryValue[]>;

export abstract class BaseApiService {
  private readonly baseUrl: string;
  private readonly resource: string;
  private readonly language: string;

  protected constructor(resource: string) {
    this.resource = resource;
    this.language = inject(LOCALE_ID);
    const url = inject(SERVER_APP_CONFIG, {optional: true})?.internalApiUrl
      ?? inject(WEB_APP_CONFIG).apiUrl;
    this.baseUrl = `${url.endsWith("/") ? url : url + "/"}api/`;
  }

  private get resourceUrl(): string {
    return this.baseUrl + this.resource + "/" + this.language + "/";
  }

  protected getEndpointUrl(...path: readonly string[]): string {
    return this.resourceUrl + path.join("/");
  }

  protected queryParametersFromPaginationQuery(paginationQuery?: IPaginationQuery): QueryParameters {
    if (!paginationQuery) return {};
    const queryParameters: QueryParameters = {};
    if (paginationQuery.offset !== undefined)
      queryParameters["offset"] = paginationQuery.offset;
    if (paginationQuery.count !== undefined)
      queryParameters["count"] = paginationQuery.count;
    if (paginationQuery.sortBy !== undefined) {
      if (typeof paginationQuery.sortBy === "object") {
        queryParameters["sort-by"] = paginationQuery.sortBy.field;
        queryParameters["sort-in"] = paginationQuery.sortBy.direction;
      } else {
        queryParameters["sort-by"] = paginationQuery.sortBy;
      }
    }
    return queryParameters;
  }

  protected httpParamsFromQueryParameters(queryParameters: Readonly<QueryParameters> = {}): HttpParams | undefined {
    let hasParameters = false;
    for (const key in queryParameters) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (queryParameters[key] !== undefined) {
        hasParameters = true;
        break;
      }
    }
    return hasParameters ? new HttpParams({fromObject: queryParameters}) : undefined;
  }

  protected httpParamsFromPaginationQuery(paginationQuery?: IPaginationQuery): HttpParams | undefined {
    return this.httpParamsFromQueryParameters(this.queryParametersFromPaginationQuery(paginationQuery));
  }
}
